import type { Application, ApplicationStage } from '../types/application';

type AppWithProgress = Application & {
  formProgress?: { total: number; completed: number } | null;
  documentProgress?: { total: number; completed: number } | null;
};

/** Before post-payment application work (forms + required documents). */
const PRE_APP_WORK_STAGES: ApplicationStage[] = [
  'eligibility_check',
  'eligibility_completed',
  'qualified',
  'disqualified',
  'package_selected',
  'uscis_fee_decision',
  'uscis_fee_questionnaire',
  'uscis_fee_ready',
  'payment_pending',
];

const STAGE3_COMPLETED_STAGES: ApplicationStage[] = [
  'forms_completed',
  'under_review',
  'revision_requested',
  'ready_for_uscis',
  'uscis_form_generated',
  'signature_pending',
  'signature_completed',
  'submitted_to_uscis',
  'approved',
  'rejected',
  'closed',
];

const STAGE4_COMPLETED_STAGES: ApplicationStage[] = [
  'ready_for_uscis',
  'uscis_form_generated',
  'signature_pending',
  'signature_completed',
  'submitted_to_uscis',
  'approved',
  'rejected',
  'closed',
];

const STAGE5_COMPLETED_STAGES: ApplicationStage[] = [
  'signature_completed',
  'submitted_to_uscis',
  'approved',
  'rejected',
  'closed',
];

function formsDocTotals(app: AppWithProgress): {
  formTotal: number;
  formCompleted: number;
  docTotal: number;
  docCompleted: number;
} {
  const formTotal = app.formProgress?.total ?? 0;
  const formCompleted = app.formProgress?.completed ?? 0;
  const docTotal = app.documentProgress?.total ?? 0;
  const docCompleted = app.documentProgress?.completed ?? 0;
  return { formTotal, formCompleted, docTotal, docCompleted };
}

function getCompletionPercent(completed: number, total: number): number {
  if (total <= 0) return 0;
  return Math.min(100, Math.max(0, (completed / total) * 100));
}

/**
 * Dashboard weighted progress (0-100):
 * Stage 1 (forms)      => up to 25%
 * Stage 2 (documents)  => up to 25%
 * Stage 3 (review)     => +20% once submitted/review stage reached
 * Stage 4 (cover/ready)=> +10% once ready-for-USCIS stage reached
 * Stage 5 (signature)  => +20% once signature completed stage reached
 */
export function getDashboardApplicationProgress(app: AppWithProgress): {
  totalSteps: number;
  completedSteps: number;
} {
  const stage = (app.stage || 'eligibility_check') as ApplicationStage;

  if (PRE_APP_WORK_STAGES.includes(stage)) {
    return { completedSteps: 0, totalSteps: 100 };
  }

  const { formTotal, formCompleted, docTotal, docCompleted } = formsDocTotals(app);
  const stage3Completed = STAGE3_COMPLETED_STAGES.includes(stage);
  const stage4Completed = STAGE4_COMPLETED_STAGES.includes(stage);
  const stage5Completed = STAGE5_COMPLETED_STAGES.includes(stage);

  // Always use actual completion for forms/docs; stage milestones add fixed bonuses on top.
  const formPercent = getCompletionPercent(formCompleted, formTotal);
  const docPercent = getCompletionPercent(docCompleted, docTotal);

  const formWeight = formPercent * 0.25;
  const documentWeight = docPercent * 0.25;
  const stage3Weight = stage3Completed ? 20 : 0;
  const stage4Weight = stage4Completed ? 10 : 0;
  const stage5Weight = stage5Completed ? 20 : 0;

  const weightedProgress = Math.min(
    100,
    Math.max(0, formWeight + documentWeight + stage3Weight + stage4Weight + stage5Weight)
  );

  return {
    completedSteps: Math.round(weightedProgress),
    totalSteps: 100,
  };
}
